/**
 * Small parts the Workshop's stage-1 surfaces share: who can see a project,
 * the kind tag, and the empty and failed states in the house voice (a boxed
 * glyph, a Barlow title, one line of why, one action that points somewhere).
 */
import type { ReactNode } from "react";
import { isAxiosError } from "axios";
import { AlertCircle, RotateCw, type LucideIcon } from "lucide-react";
import type { WorkshopPerson, WorkshopProject } from "@/types/workshop.type";
import { cn } from "@/utils/helpers";
import { namesList } from "./PeopleStack";

/** A request that never reached Mohtawa (offline, or no answer at all). */
export function isNetworkError(error: unknown): boolean {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
  return isAxiosError(error) && !error.response;
}

type Audience = { label: string; shared: boolean };

/** "Only you", "Shared with Rana and Omar", "Rana’s project, shared with you". */
export function projectAudience(project: {
  isOwner?: boolean;
  owner?: WorkshopPerson | null;
  members?: WorkshopPerson[];
}): Audience {
  const members = project.members ?? [];
  if (project.isOwner === false) {
    return {
      label: `${project.owner?.name?.trim() || "A teammate"}’s project, shared with you`,
      shared: true,
    };
  }
  if (members.length === 0) return { label: "Only you", shared: false };
  return { label: `Shared with ${namesList(members, 2)}`, shared: true };
}

export function isArchived(project: Pick<WorkshopProject, "status">): boolean {
  return project.status === "archived";
}

/** The mono kind tag: purple says what a thing is. */
export function KindTag({ children }: { children: ReactNode }) {
  return <span className="ws-tag ws-tag--kind">{children}</span>;
}

/** A neutral mono state tag (ARCHIVED, SAME TITLE AS ANOTHER OUTPUT). */
export function StateTag({ children }: { children: ReactNode }) {
  return <span className="ws-tag">{children}</span>;
}

/** Empty or failed, in one voice. */
export function WsStateBlock({
  icon: Icon,
  title,
  line,
  action,
  tone = "neutral",
  className,
}: {
  icon: LucideIcon;
  title: string;
  line: string;
  action?: ReactNode;
  tone?: "neutral" | "alert";
  className?: string;
}) {
  return (
    <div className={cn("ws-state", tone === "alert" && "ws-state--alert", className)}>
      <span className="ws-state-glyph" aria-hidden="true">
        <Icon size={18} strokeWidth={1.6} />
      </span>
      <h2 className="ws-state-title">{title}</h2>
      <p className="ws-state-line">{line}</p>
      {action ? <div className="ws-state-action">{action}</div> : null}
    </div>
  );
}

/** A partial failure: what loaded stays, this says what did not and retries. */
export function WsInlineError({
  title,
  line = "Your connection dropped or the server did not answer. Nothing was lost.",
  onRetry,
  className,
}: {
  title: string;
  line?: string;
  onRetry?: () => void;
  className?: string;
}) {
  return (
    <div className={cn("ws-inline-error", className)} role="alert">
      <p className="ws-inline-error-title">
        <AlertCircle size={15} strokeWidth={1.8} aria-hidden="true" />
        {title}
      </p>
      <p className="ws-inline-error-line">{line}</p>
      {onRetry ? (
        <button type="button" className="ws-retry" onClick={onRetry}>
          <RotateCw size={13} strokeWidth={2} aria-hidden="true" />
          Try again
        </button>
      ) : null}
    </div>
  );
}

/** The accent Try again used by full-page failures. */
export function RetryButton({ onRetry, label = "Try again" }: { onRetry: () => void; label?: string }) {
  return (
    <button type="button" className="ws-retry" onClick={onRetry}>
      <RotateCw size={13} strokeWidth={2} aria-hidden="true" />
      {label}
    </button>
  );
}
